import type { RefObject, VFC, VoidFunctionComponent } from 'react';
import { useSelector } from 'react-redux';
import * as _ from 'lodash';

import IssueItem from './IssueItem';
import IssuesTableHeader from './IssuesTableHeader';
import IssuesBulkSelectionBanner from './IssuesBulkSelectionBanner';
import IssuesTableLoading from './IssuesTableLoading';
import * as FallbackImage from './IssuesTableFallbackImages';
import Flex from '../../generic/Flex';
import * as IssuesV2Store from '../../../redux/issuesV2';
import type { IssueList, IssueRevisionWithIssues } from '../../../redux/issuesV2/types';
import { LoadingBoundary } from '../../generic/LoadingBoundary';
import RevisionItem from './IssueItem/RevisionItem';
import type { AsyncValue } from '../../../redux/asyncValue';
import { EmptyIssuesPage } from './EmptyIssuesPage';

type BaseProps = {
  tableRef: RefObject<HTMLDivElement>;
};

/**
 * Renders the classic Issues Inbox view.
 */
const IssuesTableIssuesBody: VFC<BaseProps & { issues: IssueList }> = ({ issues, tableRef }) => {
  const category = useSelector(IssuesV2Store.selectors.getIssueCategory);
  const { isLoading, errors } = useSelector(IssuesV2Store.selectors.getAsyncStatusFetchIssues);

  if (isLoading) {
    return <IssuesTableLoading />;
  }

  if (!_.isEmpty(errors)) {
    return <FallbackImage.Error />;
  }

  if (_.isEmpty(issues)) {
    return <EmptyIssuesPage />;
  }

  return (
    <div className="issues-table-body" ref={tableRef}>
      <Flex direction="column" grow="1">
        {
        issues.map(issue =>
          <IssueItem
            key={issue.id}
            category={category}
            issue={issue}
          />)
        }
      </Flex>
    </div>);
};

/**
 * Renders the issues inbox grouped by revision.
 */
const IssuesTableRevisionsBody: VFC<BaseProps & {
  revisionsValue: AsyncValue<Array<IssueRevisionWithIssues<IssueList>>>
}> = ({ revisionsValue, tableRef }) => {
  return (
    <LoadingBoundary
      asyncValue={revisionsValue}
      renderValue={({ value: revisions }) =>
        _.isEmpty(revisions)
          ?
            <EmptyIssuesPage />
          :
            <div className="issues-table-body" ref={tableRef}>
              <Flex direction="column" grow="1">
                { revisions.map(revision =>
                  <RevisionItem revision={revision} key={revision.revisionId} />,
                )}
              </Flex>
            </div>}
      renderError={({ errors }) =>
        <div className="issues-table-body" ref={tableRef}>
          <Flex direction="column" grow="1">
            { errors }
          </Flex>
        </div>}
      renderLoading={<IssuesTableLoading />}
      // We shouldn't ever hit this, but just in case...
      renderEmpty={<IssuesTableLoading />}
    />);
};

/**
 * Represents the actual "body" of the table.
 * This will pull in the Redux state to determine if we need
 * to display the loading UI, or any fallback images.
 */
const IssuesTableBody: VoidFunctionComponent<BaseProps> = ({ tableRef }) => {
  const items = useSelector(IssuesV2Store.selectors.getItems);

  switch (items.grouping) {
    case 'issue':
      return (<IssuesTableIssuesBody issues={items.issues} tableRef={tableRef} />);
    case 'revision':
      return (<IssuesTableRevisionsBody revisionsValue={items.revisions} tableRef={tableRef} />);
    default:
      return <>Unknown issue grouping.</>;
  }
};

/**
 * Dumb top-level component which renders the table
 * with each of the components in the correct order.
 */
const IssuesTable: VoidFunctionComponent<BaseProps> = ({ tableRef }) => (
  <div className="issues-table">
    <IssuesTableHeader />
    <IssuesBulkSelectionBanner />
    <IssuesTableBody tableRef={tableRef} />
  </div>
);

export default IssuesTable;
